<template>
  <ActionButton
    class="order-or-shipment-template-action-button"
    mode="secondary"
    :actions="actions"
    :disabled="disabled"
    :loading="loading"
  />
</template>

<script lang="ts" setup>
import { PrimeIcons } from '@primevue/core/api';
import { computed, PropType, Ref, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import authorization from '@rygen/rygen-ui/authorization';
import { useDeleteConfirmation } from '@rygen/rygen-ui/composables';
import { RygenMenuItem } from '@rygen/rygen-ui/model';

import {
  Order,
  OrderOrShipmentTemplate,
  OrderOrShipmentTemplateAction,
  OrderOrShipmentTemplateActionCommand,
  Shipment,
} from '@/model';
import { useParentEntityStore } from '@/stores/ParentEntity';

import ActionButton from '@rygen/rygen-ui/components/button/ActionButton.vue';

const props = defineProps({
  selectedTemplate: {
    type: Object as PropType<OrderOrShipmentTemplate>,
    default: () => new OrderOrShipmentTemplate(),
  },
  loading: {
    type: Boolean,
    default: () => false,
  },
  disabled: {
    type: Boolean,
    default: () => false,
  },
  edited: {
    type: Boolean,
    default: () => false,
  },
});
const { selectedTemplate, loading, disabled, edited } = toRefs(props);
const emit = defineEmits<{
  (event: 'command', command: OrderOrShipmentTemplateActionCommand): void;
}>();

const { t } = useI18n();
const { requestDeleteConfirmation } = useDeleteConfirmation();
const { parentEntity } = useParentEntityStore();

const isDefaultTemplate = computed(() => {
  if (parentEntity.value instanceof Order) {
    return selectedTemplate.value.defaultOrderTemplate;
  } else if (parentEntity.value instanceof Shipment) {
    return selectedTemplate.value.defaultShipmentTemplate;
  } else {
    console.warn(
      'parentEntity is not an order or shipment.',
      parentEntity.value,
    );
    return false;
  }
});

const hasTemplateCreatePermission = computed(() =>
  authorization.hasAuthority('orderTemplate', 'create'),
);

const hasTemplateUpdatePermission = computed(() =>
  authorization.hasAuthority('orderTemplate', 'update'),
);

const hasTemplateDeletePermission = computed(() =>
  authorization.hasAuthority('orderTemplate', 'delete'),
);

const actions: Ref<RygenMenuItem[]> = computed(() => [
  {
    key: OrderOrShipmentTemplateAction.SAVE,
    command: () =>
      emit('command', { action: OrderOrShipmentTemplateAction.SAVE }),
    icon: PrimeIcons.SAVE,
    label: t('common.saveParam', { param: t('templates.templates') }),
    visible:
      hasTemplateUpdatePermission.value &&
      edited.value &&
      !!selectedTemplate.value?.id,
  },
  {
    key: OrderOrShipmentTemplateAction.SAVE_AS,
    command: () =>
      emit('command', { action: OrderOrShipmentTemplateAction.SAVE_AS }),
    icon: PrimeIcons.SAVE,
    label: t('common.saveParamAs', { param: t('templates.templates') }),
    visible:
      hasTemplateCreatePermission.value &&
      (edited.value || !!selectedTemplate.value?.id),
  },
  {
    key: OrderOrShipmentTemplateAction.RENAME,
    command: () =>
      emit('command', { action: OrderOrShipmentTemplateAction.RENAME }),
    icon: PrimeIcons.PENCIL,
    label: t('common.renameParam', { param: t('templates.templates') }),
    visible: hasTemplateUpdatePermission.value && !!selectedTemplate.value?.id,
  },
  {
    key: OrderOrShipmentTemplateAction.DISCARD_CHANGES,
    command: () =>
      emit('command', {
        action: OrderOrShipmentTemplateAction.DISCARD_CHANGES,
      }),
    icon: PrimeIcons.UNDO,
    label: t('common.discardChanges', 2),
    visible: edited.value && !!selectedTemplate.value?.id,
  },
  {
    key: OrderOrShipmentTemplateAction.SET_DEFAULT,
    command: () =>
      emit('command', { action: OrderOrShipmentTemplateAction.SET_DEFAULT }),
    icon: PrimeIcons.STAR,
    label: t('templates.setDefaultTemplate'),
    visible:
      hasTemplateUpdatePermission.value &&
      !!selectedTemplate.value?.id &&
      !isDefaultTemplate.value,
  },
  {
    key: OrderOrShipmentTemplateAction.REMOVE_DEFAULT,
    command: () =>
      emit('command', { action: OrderOrShipmentTemplateAction.REMOVE_DEFAULT }),
    icon: PrimeIcons.STAR_FILL,
    label: t('templates.removeDefaultTemplate'),
    visible:
      hasTemplateUpdatePermission.value &&
      !!selectedTemplate.value?.id &&
      isDefaultTemplate.value,
  },
  {
    key: OrderOrShipmentTemplateAction.DELETE,
    class: 'text-danger',
    command: () => {
      requestDeleteConfirmation({
        param: t('templates.templates'),
        accept: () =>
          emit('command', { action: OrderOrShipmentTemplateAction.DELETE }),
      });
    },
    icon: PrimeIcons.TRASH,
    label: t('common.deleteParam', { param: t('templates.templates') }),
    visible:
      hasTemplateDeletePermission.value &&
      !!selectedTemplate.value?.id &&
      !selectedTemplate.value.defaultOrderTemplate,
  },
]);
</script>
